import { action, makeObservable, observable } from 'mobx';

import DisturbanceApi from '@api/DisturbanceApi';
import {DisturbanceTableRows, DisturbanceType, DisturbanceStatus} from '@config/DisturbanceConfig';
import LanguageStore from '@stores/LanguageStore';
import RootStore from '@stores/RootStore';
import SsoStore from '@stores/SsoStore';
import { FilterState } from '@config/DisturbanceFilter';
import { getAuthType, removeNullFilterValues } from '@util/ApiUtil';

export default class DisturbanceStore {
  @observable public disturbances: DisturbanceTableRows;
  @observable public isLoading: boolean = true;
  @observable public loadingError: boolean = false;
  @observable public filter: FilterState = null;
  public apiCall: DisturbanceType = null;

  private readonly languageStore: LanguageStore;
  private readonly ssoStore: SsoStore;

  public constructor(rootStore: RootStore) {
    makeObservable(this);
    this.languageStore = rootStore.languageStore;
    this.ssoStore = rootStore.ssoStore;
  }

  @action
  public async fetchDisturbances(filter: FilterState, offset: number = 0): Promise<void> {
    removeNullFilterValues(filter);
    const filters = { ...filter };
    if (this.apiCall === DisturbanceType.ProblemDisturbanceInfo) {
      delete filters.customerId;
      delete filters.region;
    }
    if (this.apiCall !== DisturbanceType.ProblemDisturbanceInfo && filters.status && filters.status.includes(DisturbanceStatus.DEADEND)) {
      filters.status = filters.status.filter((status: string) => status !== DisturbanceStatus.DEADEND);
    }
    try {
      this.resetState();
      switch (this.apiCall) {
        case DisturbanceType.ChangeDisturbanceInfo:
          this.disturbances = await DisturbanceApi.getChanges(offset, filters, getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
          break;
        case DisturbanceType.ProblemDisturbanceInfo:
          this.disturbances = await DisturbanceApi.getProblems(offset, filters, getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
          break;
        case DisturbanceType.IncidentDisturbanceInfo:
          this.disturbances = await DisturbanceApi.getIncidents(offset, filters, getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
          break;
        default:
      }
    } catch (err) {
      console.error(err);
      this.loadingError = true;
      if (this.disturbances !== undefined) {
        this.disturbances.offset.currentPage = offset;
      }
    } finally {
      this.isLoading = false;
    }
  }

  @action
  public resetDisturbances = (): void => {
    this.disturbances = undefined;
    this.isLoading = true;
  };

  private resetState = (): void => {
    if (this.disturbances !== undefined) {
      this.disturbances.data = null;
      this.disturbances.offset.currentPage = null;
      this.disturbances.offset.prevPage = null;
      this.disturbances.offset.nextPage = null;
    }
    this.isLoading = true;
    this.loadingError = false;
  };
}
